import React, { useEffect, useState } from "react";
import { Plus, X, Trash2, Edit2, Save } from "lucide-react";
import { api, fetchEventTypes } from "../../lib/api";
import { ImageUpload } from "../../components/ImageUpload";

const blank = {
  title: "",
  title_pt: "",
  type: "wrestling",
  date: "",
  city: "",
  venue: "",
  description: "",
  description_pt: "",
  image: "",
  ticket_url: "",
  ticket_price: "",
  is_free: false,
  ticket_mode: "stripe",
};

export const AdminEventsTab = ({ events, refresh }) => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [typeOpts, setTypeOpts] = useState([
    { v: "wrestling", l: "WRESTLING" },
    { v: "music", l: "MUSIC" },
    { v: "mixed", l: "MIXED" },
  ]);

  useEffect(() => {
    fetchEventTypes()
      .then((d) => {
        const opts = (d.types || []).map((t) => ({ v: t.slug, l: (t.label_en || t.slug).toUpperCase() }));
        if (opts.length) setTypeOpts(opts);
      })
      .catch(() => {});
  }, []);

  const reset = () => {
    setForm(blank);
    setEditingId(null);
    setShow(false);
    setError("");
  };

  const startEdit = (e) => {
    setEditingId(e.id);
    setForm({
      title: e.title || "",
      title_pt: e.title_pt || "",
      type: e.type || "wrestling",
      date: e.date ? e.date.slice(0, 16) : "",
      city: e.city || "",
      venue: e.venue || "",
      description: e.description || "",
      description_pt: e.description_pt || "",
      image: e.image || "",
      ticket_url: e.ticket_url || "",
      ticket_price: e.ticket_price ?? "",
      is_free: e.is_free === true,
      ticket_mode: e.ticket_mode || (e.is_free ? "free" : e.ticket_url ? "external" : "stripe"),
    });
    setShow(true);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const mode = form.ticket_mode || "stripe";
      const payload = {
        ...form,
        date: form.date && form.date.length === 16 ? form.date + ":00" : form.date,
        ticket_mode: mode,
        is_free: mode === "free",
        ticket_price: mode === "free" || form.ticket_price === "" ? null : Number(form.ticket_price),
        ticket_url: mode === "external" ? (form.ticket_url || "") : null,
      };
      if (editingId) await api.patch(`/admin/events/${editingId}`, payload);
      else await api.post("/admin/events", payload);
      reset();
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/admin/events/${id}`);
      await refresh();
    } catch {
      // ignore
    }
  };

  return (
    <section data-testid="admin-events-section">
      <div className="flex items-center justify-between mb-4">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ EVENTS</p>
        <button onClick={() => (show ? reset() : setShow(true))} className="bg-kult-pink text-black px-5 py-2 font-display text-lg hover:bg-kult-yellow inline-flex items-center gap-1" data-testid="admin-new-event-toggle">
          {show ? <X size={16} /> : <Plus size={16} />} {show ? "CLOSE" : "NEW EVENT"}
        </button>
      </div>

      {show && (
        <form onSubmit={submit} className="border-2 border-kult-pink p-6 bg-kult-black2 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-new-event-form">
          <h3 className="md:col-span-2 font-display text-2xl">{editingId ? "EDIT EVENT" : "NEW EVENT"}</h3>
          <Field label="TITLE (EN)" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} testId="new-event-title" />
          <Field label="TITLE (PT)" value={form.title_pt} onChange={(e) => setForm({ ...form, title_pt: e.target.value })} testId="new-event-title-pt" />
          <Select label="TYPE" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} options={typeOpts} testId="new-event-type" />
          <Field label="DATE & TIME" type="datetime-local" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} testId="new-event-date" />
          <Field label="CITY" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} testId="new-event-city" />
          <Field label="VENUE" required value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} testId="new-event-venue" />
          <Select label="TICKET MODE" value={form.ticket_mode || "stripe"} onChange={(e) => setForm({ ...form, ticket_mode: e.target.value, is_free: e.target.value === "free" })}
            options={[
              { v: "stripe", l: "PAID — CHECKOUT VIA KULTOPIA (Stripe)" },
              { v: "external", l: "PAID — EXTERNAL LINK (3rd party)" },
              { v: "free", l: "FREE / GRÁTIS (no checkout)" },
            ]}
            testId="new-event-mode"
          />
          {form.ticket_mode === "stripe" && (
            <Field label="TICKET PRICE (€)" type="number" min="0" step="0.5" required value={form.ticket_price} onChange={(e) => setForm({ ...form, ticket_price: e.target.value })} testId="new-event-price" />
          )}
          {form.ticket_mode === "external" && (
            <Field label="EXTERNAL TICKET URL (required)" type="url" required value={form.ticket_url} onChange={(e) => setForm({ ...form, ticket_url: e.target.value })} testId="new-event-ticket-url" placeholder="https://..." />
          )}
          <Textarea label="DESCRIPTION (EN)" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-2" testId="new-event-description" />
          <Textarea label="DESCRIPTION (PT)" value={form.description_pt} onChange={(e) => setForm({ ...form, description_pt: e.target.value })} className="md:col-span-2" testId="new-event-description-pt" />
          <div className="md:col-span-2">
            <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">POSTER IMAGE</p>
            <ImageUpload value={form.image ? [form.image] : []} onChange={(imgs) => setForm({ ...form, image: imgs[0] || "" })} max={1} testIdPrefix="event-image" recommendedSize="1600×900 (16:9 landscape poster)" />
          </div>
          {error && <p className="md:col-span-2 font-mono text-xs text-kult-pink">{error}</p>}
          <button type="submit" disabled={submitting} className="md:col-span-2 bg-kult-pink text-black font-display text-xl py-3 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="new-event-submit">
            <Save size={18} /> {submitting ? "..." : editingId ? "SAVE CHANGES" : "CREATE EVENT"}
          </button>
        </form>
      )}

      <div className="space-y-3" data-testid="admin-events-list">
        {events.map((e) => (
          <article key={e.id} className="border border-kult-border p-4 hover:border-kult-pink flex gap-4 items-center" data-testid={`admin-event-${e.id}`}>
            {e.image && <img src={e.image} alt="" className="w-20 h-20 object-cover grayscale" />}
            <div className="flex-1 min-w-0">
              <p className="font-mono text-[10px] tracking-[0.25em] text-kult-yellow">
                {e.type?.toUpperCase()}
                {e.is_free && <span className="ml-2 bg-kult-yellow text-black px-1.5 py-0.5">FREE</span>}
              </p>
              <h3 className="font-display text-2xl leading-tight truncate">{e.title}</h3>
              <p className="font-mono text-[11px] text-kult-muted mt-1">
                {new Date(e.date).toLocaleDateString()} · {e.city} / {e.venue}
              </p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => startEdit(e)} className="p-2 border border-kult-border hover:border-kult-pink hover:text-kult-pink" data-testid={`admin-event-edit-${e.id}`}>
                <Edit2 size={14} />
              </button>
              <button onClick={() => del(e.id)} className="p-2 border border-kult-border hover:border-kult-pink hover:text-kult-pink" data-testid={`admin-event-delete-${e.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

function Field({ label, testId, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <input {...props} data-testid={testId} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" />
    </label>
  );
}
function Textarea({ label, testId, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <textarea rows={3} {...props} data-testid={testId} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" />
    </label>
  );
}
function Select({ label, options, testId, ...props }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <select {...props} data-testid={testId} className="w-full bg-kult-black border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
